import {
    type FC,
    useContext,
    useState,
    type ReactNode,
    type Dispatch,
    type SetStateAction,
    useEffect,
    useCallback,
} from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'dibs-react-intl';
import { useRelayEnvironment, useFragment, useRefetchableFragment, graphql } from 'react-relay';
import { ClientCookieManager } from 'dibs-consented-storage';
import classnames from 'classnames';

import { executeCaptcha } from 'dibs-recaptcha/exports/recaptcha.ts';
import { Input } from 'dibs-elements/exports/Input.tsx';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect.tsx';
import { SocialNetworksList } from 'dibs-social-networks/exports/SocialNetworks.tsx';
import { type SocialDetails } from 'dibs-social-networks/src/socialNetworksHelpers.ts';
import FormButton from './sharedComponents/FormButton.tsx';

import SellerFormContext from './SellerFormContext.tsx';

import UserAgreement from './sharedComponents/UserAgreement.tsx';
import FirstName from './sharedComponents/FirstName.tsx';
import LastName from './sharedComponents/LastName.tsx';
import Email from './sharedComponents/Email.tsx';
import PhoneNumber from './sharedComponents/PhoneNumber.tsx';
import State from './sharedComponents/State.tsx';
import ZipCode from './sharedComponents/ZipCode.tsx';
import Country from './sharedComponents/Country.tsx';
import City from './sharedComponents/City.tsx';
import InputsWrapper from './sharedComponents/InputsWrapper.tsx';

import { submitSellerApplicationLeadMutation } from './mutations/submitSellerApplicationLead.ts';
import {
    type SocialNetworkList,
    sellerApplicationFormFieldsValidation,
} from '../helpers/sellerApplicationFormValidator.ts';
import { shouldShowStateField, sellerApplicationBusinessDetailsAbTest } from '../helpers/common.ts';
import { trackSellerApplicationEvent } from '../helpers/sellerApplicationTracking.ts';
import { useGetYesNoOptions } from '../helpers/sellerApplicationOptions.tsx';

import { type ContactDetailsKeys } from './SellerApplicationFormReducer.ts';
import { type SellerApplicationContactDetails_viewer$key } from './__generated__/SellerApplicationContactDetails_viewer.graphql.ts';
import { type SellerApplicationContactDetailsRefetchable_viewer$key } from './__generated__/SellerApplicationContactDetailsRefetchable_viewer.graphql.ts';
import { type IsCountryInEuRefetchQuery } from './__generated__/IsCountryInEuRefetchQuery.graphql.ts';

import dibsCss from 'dibs-css';
import {
    StreetAddressAutocomplete,
    type Address,
} from 'dibs-address-form/exports/StreetAddressAutocomplete.ts';

type SocialDataType = Record<SocialDetails['network'], string>;

export const stepTitle = (
    <FormattedMessage
        defaultMessage="Contact Information"
        id="SellerApplicationForm.contactDetails.title"
    />
);

const messages = defineMessages({
    businessNamePlaceholder: {
        id: 'SellerApplicationForm.contactDetails.businessName',
        defaultMessage: 'Business Name',
    },
    eoriPlaceholder: {
        id: 'SellerApplicationForm.contactDetails.eori',
        defaultMessage: 'EORI',
    },
    hasWebsiteOrSocialMediaPlaceholder: {
        id: 'SellerApplicationForm.contactDetails.hasWebsiteOrSocialMediaLabel',
        defaultMessage: 'Do you have a website or social media?',
    },
    socialInstructions: {
        id: 'SellerApplicationForm.contactDetails.socialNetworksInstruction',
        defaultMessage: 'Add at least one link that best represents your inventory',
    },
    streetAddress: {
        id: 'DealerApplicationForm.StreetAddressAutocomplete.placeholder',
        defaultMessage: 'Street Address',
    },
});

type ErrorType = { [fieldName in ContactDetailsKeys]?: ReactNode };

const viewerFragment = graphql`
    fragment SellerApplicationContactDetails_viewer on Viewer {
        ...SellerApplicationContactDetailsRefetchable_viewer @arguments(country: "")
        geographySalesforce(contextName: "SELLER_APPLICATION") {
            ...State_geographySalesforce
            ...Country_geographySalesforce
        }
    }
`;

const isCountryInEuQuery = graphql`
    fragment SellerApplicationContactDetailsRefetchable_viewer on Viewer
    @argumentDefinitions(country: { type: "String!" })
    @refetchable(queryName: "IsCountryInEuRefetchQuery") {
        isCountryInEu(country: $country)
    }
`;

type Props = {
    viewer: SellerApplicationContactDetails_viewer$key;
    setFormError: Dispatch<SetStateAction<boolean>>;
};

const SellerApplicationContactDetails: FC<Props> = ({ viewer: viewerRef, setFormError }) => {
    const environment = useRelayEnvironment();
    const intl = useIntl();
    const { state, dispatch } = useContext(SellerFormContext);
    const viewer = useFragment(viewerFragment, viewerRef);
    const [{ isCountryInEu }, refetch] = useRefetchableFragment<
        IsCountryInEuRefetchQuery,
        SellerApplicationContactDetailsRefetchable_viewer$key
    >(isCountryInEuQuery, viewer);

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessages, setErrorMessages] = useState<ErrorType>({});

    const yesNoOptions = useGetYesNoOptions();

    const {
        businessName,
        firstName,
        lastName,
        phoneNumber,
        email,
        country,
        line1,
        zipCode,
        city,
        state: countryState,
        eori,
        hasWebsiteOrSocialMedia,
        socialNetworks,
    } = state.formDataContactDetails;
    const geographySalesforce = viewer?.geographySalesforce;
    const showStateField = shouldShowStateField(country);
    const showEORIField = !showStateField && isCountryInEu;

    useEffect(() => {
        refetch({ country });
    }, [refetch, country]);

    const validateField = useCallback(
        (name: ContactDetailsKeys, value: string | boolean | SocialNetworkList): void => {
            const { errors } = sellerApplicationFormFieldsValidation({ [name]: value });
            const error = errors?.[name];

            setErrorMessages({
                ...errorMessages,
                [name]: error,
            });
        },
        [errorMessages]
    );

    async function submit(): Promise<void> {
        const { isValid, errors } = sellerApplicationFormFieldsValidation({
            businessName,
            firstName,
            lastName,
            phoneNumber,
            email,
            country,
            line1,
            city,
            zipCode,
            hasWebsiteOrSocialMedia,
            ...(hasWebsiteOrSocialMedia && { socialNetworks }),
            ...(showStateField && { state: countryState }),
            ...(showEORIField && { eori }),
        });
        if (isValid) {
            setIsLoading(true);
            setErrorMessages({});
            const socialData = (socialNetworks || []).reduce<SocialDataType>((acc, network) => {
                acc = { ...acc, [network.network.toLowerCase()]: network.socialUserName };
                return acc;
            }, {} as SocialDataType);
            try {
                const captchaAction = 'SELLER_APPLICATION';

                const { captchaKey, captchaToken, captchaRequestUuid } = await executeCaptcha({
                    action: captchaAction,
                    featureFlag: 'sellerApplicationCaptcha',
                    featureFlagV3: 'sellerApplicationCaptcha',
                    relayEnvironment: environment,
                });

                let leadSourceDetail = null;

                if (sellerApplicationBusinessDetailsAbTest) {
                    leadSourceDetail = `Seller-Application-Business-Details - ${sellerApplicationBusinessDetailsAbTest}`;
                }

                const response = await submitSellerApplicationLeadMutation(environment, {
                    captchaAction,
                    captchaKey,
                    captchaToken,
                    captchaRequestUuid,
                    company: businessName,
                    firstName,
                    lastName,
                    phone: phoneNumber,
                    email,
                    country,
                    street: line1,
                    postalCode: zipCode,
                    city,
                    ...(showStateField && { state: countryState }),
                    ...(showEORIField && { eori }),
                    ...(leadSourceDetail && { leadSourceDetail }),
                    ...socialData,
                });

                if (!response?.SubmitSellerApplicationLead?.pass) {
                    throw Error('Google captcha validation error');
                }

                const leadId = response?.SubmitSellerApplicationLead?.leadId;

                // Set Cookie for loading from Salesforce, expires in 90 days
                ClientCookieManager.set('sellerLeadId', leadId, { expires: 90 });
                trackSellerApplicationEvent({
                    type: 'professional',
                    action: 'step 1 submit',
                    interactionName: 'submit_page_1',
                    stepInteractionType: 'click on Continue',
                });
                dispatch({
                    type: 'SET_CREATED_LEAD_ID',
                    leadId: leadId || '',
                });
                dispatch({ type: 'SET_STEP', step: 'BUSINESS_STEP2' });
            } catch (err) {
                setIsLoading(false);
                setFormError(true);
            }
        } else {
            setIsLoading(false);
            setErrorMessages(errors);
        }
    }

    const setFormField = useCallback(
        (field: ContactDetailsKeys, value: string | boolean | SocialNetworkList): void => {
            if (field === 'country' && value !== 'US') {
                dispatch({
                    type: 'SET_FORM_CONTACT_DETAILS',
                    formDataContactDetails: { state: '' },
                });
            } else if (field === 'country' && value === 'US') {
                dispatch({
                    type: 'SET_FORM_CONTACT_DETAILS',
                    formDataContactDetails: { eori: '' },
                });
            }

            dispatch({
                type: 'SET_FORM_CONTACT_DETAILS',
                formDataContactDetails: { [field]: value },
            });
        },
        [dispatch]
    );

    const setField = useCallback(
        (name: ContactDetailsKeys, value: string | boolean | SocialNetworkList): void => {
            setFormField(name, value);
            validateField(name, value);
        },
        [setFormField, validateField]
    );

    const handleAddressSelect = (addressDetails: Address): void => {
        const {
            line1: addressLine1,
            country: addressCountry,
            city: addressCity,
            state: addressState,
            zipCode: addressZipCode,
        } = addressDetails || {};
        setField('line1', addressLine1 || '');
        setField('country', addressCountry || '');
        setField('city', addressCity || '');
        setField('state', addressState || '');
        setField('zipCode', addressZipCode || '');
    };

    return (
        <div className={dibsCss.mbLarge} data-tn="application-form-business-details">
            <div className={dibsCss.wFull}>
                <InputsWrapper>
                    <FirstName
                        errorMessage={errorMessages?.firstName}
                        isLoading={isLoading}
                        onChange={e => {
                            const value = e.target.value;
                            setField('firstName', value);
                        }}
                        value={firstName}
                    />
                    <LastName
                        onChange={e => {
                            const value = e.target.value;
                            setField('lastName', value);
                        }}
                        isLoading={isLoading}
                        errorMessage={errorMessages?.lastName}
                        value={lastName}
                    />
                </InputsWrapper>
                <div className={dibsCss.mbSmall}>
                    <Email
                        isLoading={isLoading}
                        onChange={e => {
                            const value = e.target.value;
                            setField('email', value);
                        }}
                        errorMessage={errorMessages?.email}
                        value={email}
                    />
                </div>
                <div className={dibsCss.mbSmall}>
                    <PhoneNumber
                        isLoading={isLoading}
                        onChange={({ value, isValid }) => {
                            setField('phoneNumber', isValid ? value : '');
                        }}
                        errorMessage={errorMessages?.phoneNumber}
                        value={phoneNumber}
                    />
                </div>
                <div className={dibsCss.mbSmall}>
                    <Input
                        placeholder={intl.formatMessage(messages.businessNamePlaceholder)}
                        hasAnimatedPlaceholder
                        dataTn="business-name"
                        name="business-name"
                        onChange={e => {
                            const value = e.target.value;
                            setField('businessName', value);
                        }}
                        errorMessage={errorMessages?.businessName}
                        hasError={!!errorMessages?.businessName}
                        disabled={isLoading}
                        value={businessName}
                    />
                </div>
                <div className={dibsCss.mbSmall}>
                    <StreetAddressAutocomplete
                        value={line1}
                        onSelect={handleAddressSelect}
                        onChange={e => {
                            const value = e.target.value;
                            setField('line1', value);
                        }}
                        errorMessage={errorMessages?.line1}
                        placeholder={intl.formatMessage(messages.streetAddress)}
                        disabled={isLoading}
                        size="large"
                    />
                </div>
                <InputsWrapper>
                    <Country
                        geographySalesforce={geographySalesforce || null}
                        onChange={e => {
                            const value = e.target.value;
                            setField('country', value);
                        }}
                        errorMessage={errorMessages?.country}
                        disabled={isLoading}
                        value={country}
                    />
                    <ZipCode
                        disabled={isLoading}
                        value={zipCode}
                        onChange={e => {
                            const value = e.target.value;
                            setField('zipCode', value);
                        }}
                        errorMessage={errorMessages?.zipCode}
                    />
                </InputsWrapper>
                <InputsWrapper>
                    <City
                        onChange={e => {
                            const value = e.target.value;
                            setField('city', value);
                        }}
                        disabled={isLoading}
                        value={city}
                        errorMessage={errorMessages?.city}
                    />
                    {country && country === 'US' ? (
                        <State
                            onChange={e => {
                                const value = e.target.value;
                                setField('state', value);
                            }}
                            errorMessage={errorMessages?.state}
                            value={countryState}
                            geographySalesforce={geographySalesforce || null}
                            disabled={isLoading}
                        />
                    ) : null}
                </InputsWrapper>
                {showEORIField ? (
                    <div className={dibsCss.mbSmall}>
                        <Input
                            placeholder={intl.formatMessage(messages.eoriPlaceholder)}
                            hasAnimatedPlaceholder
                            dataTn="business-eori"
                            name="business-eori"
                            onChange={e => {
                                const value = e.target.value;
                                setField('eori', value);
                            }}
                            errorMessage={errorMessages?.eori}
                            hasError={!!errorMessages?.eori}
                            disabled={isLoading}
                            value={eori}
                            // Throughout the form we use maxLength on all free text fields
                            // The Salesforce side has limits on char length and requests will fail if exceeded (Can be adjusted if needed)
                            maxLength={25}
                        />
                    </div>
                ) : null}
                <div className={dibsCss.mbSmall}>
                    <BasicSelect
                        dataTn="has-website-or-social-media"
                        name="has-website-or-social-media"
                        placeholder={intl.formatMessage(
                            messages.hasWebsiteOrSocialMediaPlaceholder
                        )}
                        hasAnimatedPlaceholder
                        options={yesNoOptions}
                        onChange={e =>
                            setField('hasWebsiteOrSocialMedia', e.target.value === 'true')
                        }
                        errorMessage={errorMessages?.hasWebsiteOrSocialMedia}
                        hasError={!!errorMessages?.hasWebsiteOrSocialMedia}
                        disabled={isLoading}
                        value={
                            typeof hasWebsiteOrSocialMedia === 'boolean'
                                ? String(hasWebsiteOrSocialMedia)
                                : undefined
                        }
                    />
                </div>
                <div
                    className={classnames(dibsCss.mbSmall, {
                        [dibsCss.hidden]: !hasWebsiteOrSocialMedia,
                    })}
                >
                    <SocialNetworksList
                        socialInstructions={intl.formatMessage(messages.socialInstructions)}
                        updateForm={useCallback(
                            (fieldName: string, fieldValue: SocialNetworkList) =>
                                setFormField('socialNetworks', fieldValue),
                            [setFormField]
                        )}
                        networks={['Website', 'Instagram', 'Facebook']}
                        errorMessage={errorMessages?.socialNetworks}
                        showError={!!errorMessages?.socialNetworks}
                    />
                </div>
                <FormButton isLoading={isLoading} onSubmit={submit} />
                <div className={dibsCss.mbXxlarge}>
                    <UserAgreement />
                </div>
            </div>
        </div>
    );
};

export default SellerApplicationContactDetails;
